/**
 * Codes-monitor store (Wave 5, spec §2 item 10): monitored jurisdiction
 * sources (list + create — the server exposes no delete), the global
 * "check now" run (POST /api/scraper/run) followed over the existing job SSE
 * pattern (/api/jobs/{id}/events), and the human review queue.
 *
 * Posture: the scraper is monitor-only. istari NEVER auto-updates rules —
 * every detected change waits in the review queue for an explicit,
 * note-carrying acknowledge/dismiss by a human.
 */
import { create } from "zustand";

import * as api from "../api/client";
import type { ReviewItem, ReviewStatus, ScraperSource } from "../api/types";
import { type EventSourceFactory, parsePayload } from "./generateFlow";

export type LoadStatus = "idle" | "loading" | "ready" | "error";

/** Progress of the global "check now" run (one job covers all sources). */
export type RunState =
  | { phase: "idle" }
  | { phase: "running"; progress: number }
  | { phase: "done" }
  | { phase: "failed"; message: string };

function message(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Follow a job's SSE stream until a terminal event (same event vocabulary as
 * the generate flow: progress / complete / error / cancelled). Resolves with
 * the terminal outcome; never rejects.
 */
export function followJob(
  jobId: string,
  onProgress: (progress: number) => void,
  makeSource: EventSourceFactory,
): Promise<{ ok: boolean; message: string | null }> {
  return new Promise((resolve) => {
    const source = makeSource(`/api/jobs/${encodeURIComponent(jobId)}/events`);
    let settled = false;
    const finish = (ok: boolean, msg: string | null) => {
      if (settled) return;
      settled = true;
      source.close();
      resolve({ ok, message: msg });
    };

    source.addEventListener("progress", (ev) => {
      const payload = parsePayload(ev.data);
      if (typeof payload.progress === "number") onProgress(payload.progress);
    });
    source.addEventListener("complete", () => finish(true, null));
    source.addEventListener("error", (ev) => {
      const payload = parsePayload(ev.data ?? "");
      finish(false, typeof payload.message === "string" ? payload.message : "check failed");
    });
    source.addEventListener("cancelled", () => finish(false, "check cancelled"));
    source.onerror = () => finish(false, "lost connection to the job stream");
  });
}

const defaultEventSource: EventSourceFactory = (url) => new EventSource(url);

export interface CodesMonitorState {
  sources: ScraperSource[];
  sourcesStatus: LoadStatus;
  sourcesError: string | null;

  reviewItems: ReviewItem[];
  reviewStatus: LoadStatus;
  reviewError: string | null;

  /** Global "check now" run state (one job checks every source). */
  run: RunState;

  /** Last mutation failure (add source, review resolution). */
  mutationError: string | null;

  loadSources: () => Promise<void>;
  loadReviewItems: () => Promise<void>;
  addSource: (jurisdiction: string, url: string) => Promise<boolean>;
  /**
   * Run one change-detection check over ALL sources and follow its job over
   * SSE; on success the sources (last-checked) and review queue are reloaded.
   */
  checkNow: (eventSource?: EventSourceFactory) => Promise<void>;
  /** Acknowledge/dismiss a review item with reviewer notes. */
  resolveItem: (itemId: string, status: ReviewStatus, notes: string) => Promise<boolean>;
}

export const useCodesMonitorStore = create<CodesMonitorState>((set, get) => ({
  sources: [],
  sourcesStatus: "idle",
  sourcesError: null,
  reviewItems: [],
  reviewStatus: "idle",
  reviewError: null,
  run: { phase: "idle" },
  mutationError: null,

  loadSources: async () => {
    set({ sourcesStatus: "loading", sourcesError: null });
    try {
      const sources = await api.listScraperSources();
      set({ sources, sourcesStatus: "ready" });
    } catch (err) {
      set({ sourcesStatus: "error", sourcesError: message(err) });
    }
  },

  loadReviewItems: async () => {
    set({ reviewStatus: "loading", reviewError: null });
    try {
      const reviewItems = await api.listReviewItems();
      set({ reviewItems, reviewStatus: "ready" });
    } catch (err) {
      set({ reviewStatus: "error", reviewError: message(err) });
    }
  },

  addSource: async (jurisdiction, url) => {
    set({ mutationError: null });
    try {
      const created = await api.createScraperSource({ jurisdiction, url });
      set((state) => ({ sources: [...state.sources, created] }));
      return true;
    } catch (err) {
      set({ mutationError: message(err) });
      return false;
    }
  },

  checkNow: async (eventSource = defaultEventSource) => {
    if (get().run.phase === "running") return;

    set({ run: { phase: "running", progress: 0 } });
    try {
      const job = await api.runScraperMonitors();
      const outcome = await followJob(
        job.id,
        (progress) =>
          set({ run: { phase: "running", progress: Math.min(100, Math.max(0, progress)) } }),
        eventSource,
      );
      if (outcome.ok) {
        set({ run: { phase: "done" } });
        // Refresh last-checked stamps and any newly queued review items.
        await Promise.all([get().loadSources(), get().loadReviewItems()]);
      } else {
        set({ run: { phase: "failed", message: outcome.message ?? "check failed" } });
      }
    } catch (err) {
      set({ run: { phase: "failed", message: message(err) } });
    }
  },

  resolveItem: async (itemId, status, notes) => {
    set({ mutationError: null });
    try {
      const trimmed = notes.trim();
      // Omit empty notes — the server leaves an omitted/null field unchanged.
      const updated = await api.patchReviewItem(itemId, {
        status,
        ...(trimmed === "" ? {} : { notes: trimmed }),
      });
      set((state) => ({
        reviewItems: state.reviewItems.map((item) => (item.id === itemId ? updated : item)),
      }));
      return true;
    } catch (err) {
      set({ mutationError: message(err) });
      return false;
    }
  },
}));
